<template>
    <div
        class="html-split-editor"
        :class="{ 'is-resizing': resizing }"
    >
        <div
            ref="panes"
            class="html-split-editor__panes"
        >
            <!-- HTML 소스 -->
            <section
                v-show="viewMode !== 'preview' && viewMode !== 'visual'"
                class="html-split-editor__pane html-split-editor__pane--source"
                :style="sourcePaneStyle"
            >
                <div class="html-split-editor__pane-header">
                    <strong class="small">HTML 소스</strong>

                    <!-- 소스 패널이 보일 때 이 헤더가 보기 모드 전환을 담당 -->
                    <b-button-group
                        size="sm"
                        aria-label="HTML 편집 보기 모드"
                    >
                        <b-button
                            v-for="option in viewModeOptions"
                            :key="`source-${option.value}`"
                            type="button"
                            variant="outline-secondary"
                            class="html-split-editor__view-button"
                            :class="{ active: viewMode === option.value }"
                            :aria-pressed="viewMode === option.value"
                            @click="setViewMode(option.value)"
                        >
                            {{ option.text }}
                        </b-button>
                    </b-button-group>
                </div>

                <div class="html-split-editor__source-body">
                    <b-form-textarea
                        ref="textarea"
                        :value="value"
                        rows="1"
                        class="html-split-editor__textarea"
                        spellcheck="false"
                        :placeholder="placeholder"
                        @input="onTextareaInput"
                        @focus="onTextareaFocus"
                    />
                </div>
            </section>

            <!-- 높이 조절 핸들 -->
            <div
                v-show="viewMode === 'split'"
                ref="separator"
                class="html-split-editor__separator"
                role="separator"
                aria-label="HTML 소스와 미리보기 높이 조절"
                aria-orientation="horizontal"
                :aria-valuemin="separatorValueMin"
                :aria-valuemax="separatorValueMax"
                :aria-valuenow="separatorValueNow"
                :aria-valuetext="separatorValueText"
                tabindex="0"
                title="드래그하여 높이 조절 · 더블클릭하여 초기화"
                @pointerdown="startResize"
                @pointermove="onPointerMove"
                @pointerup="stopResize"
                @pointercancel="stopResize"
                @lostpointercapture="stopResize"
                @dblclick.prevent="resetSplit"
                @keydown.up.prevent="nudgeSplit(-2)"
                @keydown.down.prevent="nudgeSplit(2)"
            >
                <span
                    class="html-split-editor__separator-grip"
                    aria-hidden="true"
                />
            </div>

            <!-- 실시간 미리보기 — visual 호스트에서는 편집 가능한 비주얼 캔버스(분할/비주얼 모드) -->
            <section
                v-show="viewMode !== 'source'"
                class="html-split-editor__pane html-split-editor__pane--preview"
            >
                <div class="html-split-editor__pane-header">
                    <strong class="small">{{ canvasActive ? '비주얼 편집' : '실시간 미리보기' }}</strong>

                    <!-- 소스 패널이 숨는 단독 모드에서도 다른 모드로 돌아갈 수 있게 유지 -->
                    <b-button-group
                        v-if="viewMode === 'preview' || viewMode === 'visual'"
                        size="sm"
                        aria-label="HTML 편집 보기 모드"
                    >
                        <b-button
                            v-for="option in viewModeOptions"
                            :key="`preview-${option.value}`"
                            type="button"
                            variant="outline-secondary"
                            class="html-split-editor__view-button"
                            :class="{ active: viewMode === option.value }"
                            :aria-pressed="viewMode === option.value"
                            @click="setViewMode(option.value)"
                        >
                            {{ option.text }}
                        </b-button>
                    </b-button-group>

                    <span
                        v-else
                        class="small text-muted"
                    >
                        {{ paneHint }}
                    </span>
                </div>

                <!-- iframe은 자동 높이, 이 요소만 미리보기 스크롤을 소유한다. -->
                <div class="html-split-editor__preview-scroll">
                    <MailDocCanvas
                        v-if="canvasActive"
                        ref="canvas"
                        :key="canvasEpoch"
                        class="html-split-editor__preview"
                        :value="value"
                        :values="values"
                        @input="onCanvasInput"
                        @unsupported="onCanvasUnsupported"
                    />
                    <MailDocPreview
                        v-else
                        class="html-split-editor__preview"
                        :template="previewValue == null ? value : previewValue"
                        :values="values"
                        :refresh-key="refreshKey"
                    />
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import MailDocPreview from './MailDocPreview.vue'
import MailDocCanvas from './MailDocCanvas.vue'
import splitSync from '../htmlSplitSync'

const DEFAULT_SPLIT_RATIO = 55
const SOURCE_MIN_HEIGHT = 240
const PREVIEW_MIN_HEIGHT = 260
const SEPARATOR_HEIGHT = 10

const VIEW_MODE_OPTIONS = Object.freeze([
    { value: 'source', text: '소스 크게' },
    { value: 'split', text: '분할' },
    { value: 'preview', text: '미리보기 크게' },
])
// visual 호스트: 분할의 아래 패인이 이미 편집 가능한 캔버스(MailDocCanvas)라
// '미리보기 크게' 대신 '비주얼 편집'(캔버스 단독) 을 노출한다.
const VISUAL_VIEW_MODE_OPTIONS = Object.freeze([
    VIEW_MODE_OPTIONS[0],
    VIEW_MODE_OPTIONS[1],
    { value: 'visual', text: '비주얼 편집' },
])
// textarea 타이핑 → 캔버스 재마운트 디바운스(ms). 캔버스 자체의 emit 디바운스(250ms)와 별개.
const CANVAS_SYNC_DEBOUNCE_MS = 300

export default {
    name: 'HtmlSplitEditor',
    components: { MailDocPreview, MailDocCanvas },
    props: {
        value: {
            type: String,
            default: '',
        },
        refreshKey: {
            type: [Number, String],
            default: 0,
        },
        placeholder: {
            type: String,
            default: '',
        },
        storageKey: {
            type: String,
            default: 'mail-editor:html-split-ratio',
        },
        // 미리보기에만 쓸 대체 HTML(예: 변수 치환본). null 이면 value 를 그대로 미리보기한다.
        previewValue: {
            type: String,
            default: null,
        },
        // 비주얼(MailDocCanvas) 활성화. 기본 false → 기존 3모드(소스/분할/미리보기) 그대로.
        // true 면 [소스 크게 | 분할(소스+캔버스 양방향) | 비주얼 편집] 3모드가 된다.
        visual: {
            type: Boolean,
            default: false,
        },
        // 변수 알약에 표시할 실제 값 맵 — 캔버스/미리보기로 패스스루.
        values: {
            type: Object,
            default: null,
        },
        // 최초 모드. 'visual' 은 visual prop 이 켜져 있을 때만 유효(아니면 split).
        // visual 호스트에 'preview' 가 들어오면 'visual' 로 강등한다(미리보기 모드 없음).
        defaultMode: {
            type: String,
            default: 'split',
        },
    },
    data() {
        return {
            viewMode: 'split',
            splitRatio: DEFAULT_SPLIT_RATIO,
            // 캔버스 재마운트 키 — 외부에서 value 가 바뀌면 +1 (write-once 계약).
            canvasEpoch: 0,
            // 캔버스가 방출한 마지막 HTML — 타이핑으로 인한 value 변경은 재마운트하지 않는다.
            lastCanvasEmit: null,
            // 캔버스가 편집 불가 문서로 판정 → 분할 아래 패인은 읽기 전용 미리보기로 폴백.
            canvasUnsupported: false,
            panesHeight: 0,
            resizing: false,
            activePointerId: null,
            dragOffsetY: 0,
            resizeObserver: null,
        }
    },
    computed: {
        viewModeOptions() {
            return this.visual ? VISUAL_VIEW_MODE_OPTIONS : VIEW_MODE_OPTIONS
        },
        // 아래 패인에 편집 캔버스를 띄우는가. visual 호스트의 분할/비주얼 모드에서만.
        canvasActive() {
            return this.visual && this.viewMode !== 'source' && !this.canvasUnsupported
        },
        paneHint() {
            if (this.canvasActive) return '소스와 양방향 동기화'
            if (this.canvasUnsupported) return '비주얼 편집 불가 · 미리보기'
            return '비주얼 편집과 동일 렌더링'
        },
        usablePaneHeight() {
            return Math.max(0, this.panesHeight - SEPARATOR_HEIGHT)
        },
        splitBounds() {
            const available = this.usablePaneHeight
            if (!available) return { min: 0, max: 100 }

            const min = SOURCE_MIN_HEIGHT / available * 100
            const max = (available - PREVIEW_MIN_HEIGHT) / available * 100

            if (min <= max) {
                return {
                    min: Math.max(0, min),
                    max: Math.min(100, max),
                }
            }

            // 화면이 최소 높이 합보다 작은 예외 상황에서는 두 최소값의 비율로 고정한다.
            const fixed =
                SOURCE_MIN_HEIGHT /
                (SOURCE_MIN_HEIGHT + PREVIEW_MIN_HEIGHT) *
                100
            return { min: fixed, max: fixed }
        },
        effectiveSplitRatio() {
            return this.clampSplitRatio(this.splitRatio)
        },
        sourcePaneHeight() {
            if (!this.usablePaneHeight) return SOURCE_MIN_HEIGHT

            return Math.round(
                this.usablePaneHeight * this.effectiveSplitRatio / 100
            )
        },
        sourcePaneStyle() {
            if (this.viewMode !== 'split') {
                return { flex: '1 1 auto' }
            }

            // 최초 측정 전에도 55/45에 가깝게 렌더한다.
            if (!this.panesHeight) {
                return { flex: `0 0 ${DEFAULT_SPLIT_RATIO}%` }
            }

            return { flex: `0 0 ${this.sourcePaneHeight}px` }
        },
        separatorValueMin() {
            return Math.round(this.splitBounds.min)
        },
        separatorValueMax() {
            return Math.round(this.splitBounds.max)
        },
        separatorValueNow() {
            return Math.round(this.effectiveSplitRatio)
        },
        separatorValueText() {
            const source = this.separatorValueNow
            return `소스 ${source}%, 미리보기 ${100 - source}%`
        },
    },
    watch: {
        // 부모가 value 를 바꾸면(저장 응답/초안 재생성/분할 모드의 textarea 입력) 캔버스를 다시
        // 마운트한다. 캔버스 자신이 방출한 값은 lastCanvasEmit 로 걸러 타이핑 중 재마운트를 막는다.
        // 분할 모드는 textarea 키 입력마다 재마운트하지 않도록 디바운스한다.
        value(next) {
            if (!this.visual || next === this.lastCanvasEmit) return
            if (this.viewMode === 'visual') {
                this.remountCanvas()
                return
            }
            this.canvasSync.schedule()
        },
    },
    created() {
        // 비반응 헬퍼 — textarea 편집 이후 캔버스가 재마운트될 때까지 캔버스 emit 을 stale 로 무시하고,
        // 재마운트 자체는 타이핑마다 하지 않도록 디바운스한다.
        this.sourceGuard = splitSync.createSourceGuard()
        this.canvasSync = splitSync.createDebouncedEmit(
            () => this.remountCanvas(),
            CANVAS_SYNC_DEBOUNCE_MS
        )
        this.restoreSplitRatio()
        const mode = this.visual && this.defaultMode === 'preview'
            ? 'visual'
            : this.defaultMode
        if (this.viewModeOptions.some(o => o.value === mode)) {
            this.viewMode = mode
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.measurePanes()

            if (
                this.$refs.panes &&
                typeof ResizeObserver !== 'undefined'
            ) {
                this.resizeObserver = new ResizeObserver(() => {
                    this.measurePanes()
                })
                this.resizeObserver.observe(this.$refs.panes)
            }
        })

        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.measurePanes)
        }
    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
            this.resizeObserver = null
        }

        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this.measurePanes)
        }

        this.resizing = false
        this.activePointerId = null
        this.canvasSync.cancel()
    },
    methods: {
        restoreSplitRatio() {
            if (typeof window === 'undefined') return

            try {
                const stored = window.localStorage.getItem(this.storageKey)
                const ratio = Number(stored)

                if (
                    Number.isFinite(ratio) &&
                    ratio > 0 &&
                    ratio < 100
                ) {
                    this.splitRatio = ratio
                }
            } catch (e) {
                // localStorage를 사용할 수 없는 환경에서는 기본 비율을 유지한다.
            }
        },
        persistSplitRatio() {
            if (typeof window === 'undefined') return

            try {
                window.localStorage.setItem(
                    this.storageKey,
                    String(this.splitRatio)
                )
            } catch (e) {
                // 저장 실패가 편집 기능을 막지 않게 한다.
            }
        },
        measurePanes() {
            const panes = this.$refs.panes
            const height = panes ? panes.clientHeight : 0

            if (height !== this.panesHeight) {
                this.panesHeight = height
            }
        },
        clampSplitRatio(value) {
            const number = Number(value)
            const ratio = Number.isFinite(number)
                ? number
                : DEFAULT_SPLIT_RATIO

            return Math.min(
                this.splitBounds.max,
                Math.max(this.splitBounds.min, ratio)
            )
        },
        applySplitRatio(value, persist) {
            this.splitRatio = Math.round(
                this.clampSplitRatio(value) * 10
            ) / 10

            if (persist) this.persistSplitRatio()
        },
        setViewMode(mode) {
            const valid = this.viewModeOptions.some(option => {
                return option.value === mode
            })
            if (!valid || mode === this.viewMode) return

            // 캔버스 편집 중이면 디바운스 대기 중인 편집까지 확정해 textarea 가 최신 HTML 로 뜨게.
            if (this.$refs.canvas) {
                this.$emit('input', this.getBodyText())
            }
            // 반대로 textarea → 캔버스 반영이 대기 중이면 지금 확정한다.
            if (this.canvasSync.pending()) this.remountCanvas()

            this.viewMode = mode
            this.$nextTick(() => this.measurePanes())
        },

        // ── 비주얼 캔버스(MailDocCanvas) — 분할/비주얼 모드 ────────────
        // 캔버스를 현재 value 로 다시 마운트한다(write-once 계약). 편집 불가 판정도 재시도.
        remountCanvas() {
            this.canvasSync.cancel()
            this.sourceGuard.syncCanvas()
            this.canvasUnsupported = false
            this.canvasEpoch++
        },
        // textarea 입력 — 캔버스는 이 시점부터 stale(재마운트 전까지 캔버스 emit 무시).
        // 값이 이전과 같아도(되돌린 입력) 재마운트를 예약해 revision 을 다시 맞춘다.
        onTextareaInput(html) {
            this.sourceGuard.bumpSource()
            if (this.visual) this.canvasSync.schedule()
            this.$emit('input', html)
        },
        onCanvasInput(html) {
            // 소스 편집 이후 남아 있던 캔버스의 지연 emit — 이전 HTML 이므로 버린다.
            if (!this.sourceGuard.canvasIsCurrent()) return
            this.lastCanvasEmit = html
            this.$emit('input', html)
        },
        // 캔버스가 편집 불가 문서(unknown-framed)로 판정 → 아래 패인은 미리보기로 폴백.
        // 비주얼 단독 모드였다면 분할로 강등하고 호스트에 알린다(분할에서의 재시도 실패는 조용히).
        onCanvasUnsupported(event) {
            this.canvasUnsupported = true
            if (this.viewMode !== 'visual') return
            this.viewMode = 'split'
            this.$nextTick(() => this.measurePanes())
            this.$emit('unsupported', event)
        },
        // 현재 저장용 HTML. 캔버스가 살아 있고 textarea 반영 대기가 없으면 캔버스에서 즉시
        // 직렬화(캔버스 디바운스 무관), 아니면 value(textarea 최신값).
        getBodyText() {
            const canvas = this.$refs.canvas
            if (!canvas || this.canvasSync.pending()) return this.value
            const html = canvas.getBodyText()
            this.lastCanvasEmit = html // 부모가 이 값을 v-model 에 되써도 재마운트하지 않는다.
            return html
        },
        // 저장 게이트: 캔버스가 로드 시점 변수(알약)를 잃지 않았는지. 캔버스가 없으면 검사 불가 → ok.
        validateForSave() {
            const canvas = this.$refs.canvas
            if (!canvas || this.canvasSync.pending()) return { ok: true, bodyText: this.value }
            const v = canvas.validateForSave()
            this.lastCanvasEmit = v.bodyText
            return v
        },
        // 캔버스가 살아 있으면 커서 위치에 알약 삽입 후 true. 아니면 false(호스트가 textarea 경로 사용).
        insertVariable(token, label) {
            const canvas = this.$refs.canvas
            if (!canvas || this.canvasSync.pending()) return false
            canvas.insertVariable(token, label)
            return true
        },
        onTextareaFocus() {
            this.measurePanes()
            this.$emit('focus')
        },
        startResize(event) {
            if (
                this.viewMode !== 'split' ||
                event.button !== 0
            ) {
                return
            }

            this.measurePanes()
            if (!this.panesHeight) return

            const separator = event.currentTarget
            const rect = separator.getBoundingClientRect()

            this.resizing = true
            this.activePointerId = event.pointerId
            this.dragOffsetY = event.clientY - rect.top

            if (typeof separator.setPointerCapture === 'function') {
                try {
                    separator.setPointerCapture(event.pointerId)
                } catch (e) {
                    // 포인터 캡처가 실패해도 현재 요소 안에서는 계속 조절할 수 있다.
                }
            }

            this.updateSplitFromPointer(event)
            event.preventDefault()
        },
        onPointerMove(event) {
            if (
                !this.resizing ||
                event.pointerId !== this.activePointerId
            ) {
                return
            }

            this.updateSplitFromPointer(event)
            event.preventDefault()
        },
        updateSplitFromPointer(event) {
            const panes = this.$refs.panes
            if (!panes) return

            const rect = panes.getBoundingClientRect()
            const available = Math.max(
                1,
                rect.height - SEPARATOR_HEIGHT
            )
            const sourceHeight =
                event.clientY -
                rect.top -
                this.dragOffsetY
            const ratio = sourceHeight / available * 100

            this.applySplitRatio(ratio, false)
        },
        stopResize(event) {
            if (!this.resizing) return

            const separator =
                event && event.currentTarget
                    ? event.currentTarget
                    : this.$refs.separator
            const pointerId = this.activePointerId

            this.resizing = false
            this.activePointerId = null
            this.dragOffsetY = 0

            if (
                separator &&
                pointerId != null &&
                typeof separator.hasPointerCapture === 'function' &&
                separator.hasPointerCapture(pointerId)
            ) {
                try {
                    separator.releasePointerCapture(pointerId)
                } catch (e) {
                    // 이미 해제된 포인터는 무시한다.
                }
            }

            this.persistSplitRatio()
        },
        nudgeSplit(amount) {
            this.measurePanes()
            this.applySplitRatio(
                this.effectiveSplitRatio + amount,
                true
            )
        },
        resetSplit() {
            this.splitRatio = DEFAULT_SPLIT_RATIO
            this.persistSplitRatio()
        },

        // MailEditor의 body_${idx} ref가 실제 textarea 선택 영역에 접근할 수 있게 한다.
        getTextareaElement() {
            const textarea = this.$refs.textarea
            return textarea && (textarea.$el || textarea)
        },
    },
}
</script>

<style scoped>
.html-split-editor {
    width: 100%;
    height: 76vh;
    min-height: 630px;
    max-height: 900px;
    overflow: hidden;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 4px;
}

.html-split-editor__panes {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
}

.html-split-editor__pane {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    background: #fff;
}

.html-split-editor__pane--preview {
    flex: 1 1 0;
}

.html-split-editor__pane-header {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    min-height: 38px;
    padding: 4px 8px;
    background: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

.html-split-editor__view-button {
    padding: 2px 8px;
    font-size: 12px;
    line-height: 1.4;
}

.html-split-editor__source-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.html-split-editor__textarea {
    display: block;
    width: 100%;
    height: 100% !important;
    min-height: 0 !important;
    padding: 12px 14px;
    overflow: auto;
    resize: none;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    font-family:
        SFMono-Regular,
        Menlo,
        Monaco,
        Consolas,
        "Liberation Mono",
        "Courier New",
        monospace;
    font-size: 14px;
    line-height: 1.5;
    tab-size: 4;
    overscroll-behavior: contain;
}

.html-split-editor__textarea:focus {
    border-color: transparent;
    box-shadow: inset 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.html-split-editor__separator {
    display: flex;
    flex: 0 0 10px;
    align-items: center;
    justify-content: center;
    min-height: 10px;
    cursor: row-resize;
    touch-action: none;
    background: #f1f3f5;
    border-top: 1px solid #dee2e6;
    border-bottom: 1px solid #dee2e6;
    outline: 0;
}

.html-split-editor__separator:hover,
.html-split-editor__separator:focus {
    background: #e2e6ea;
}

.html-split-editor__separator:focus {
    box-shadow: inset 0 0 0 2px rgba(0, 123, 255, 0.6);
}

.html-split-editor__separator-grip {
    display: block;
    width: 48px;
    height: 3px;
    background: #adb5bd;
    border-radius: 2px;
}

.html-split-editor.is-resizing {
    cursor: row-resize;
    user-select: none;
    -ms-user-select: none;
}

.html-split-editor__preview-scroll {
    flex: 1 1 auto;
    min-height: 0;
    padding: 0;
    overflow: auto;
    overscroll-behavior: contain;
    background: #fff;
}

.html-split-editor__preview {
    display: block;
    width: 100%;
    min-height: 100%;
}
</style>
